// ============================================================
// JavaScriptQuiz.js
//
// Gamified JavaScript Mastery Quiz — ProDev Platform
//
// Adapted from JavaQuiz.js & PythonQuiz.js — same engine,
// brand new JavaScript question bank covering:
//   Syntax & Basics, Variables & Scope, Data Types,
//   Functions, Arrays, Objects, DOM Manipulation,
//   ES6+ Features, Promises & Async, Error Handling,
//   Closures, Prototypes & OOP, Modules
//
// Features (identical engine to Java/Python quizzes):
//   - Personalised welcome screen (greets user by name)
//   - Rules & conduct screen with time limits explained
//   - Web Speech API — audible countdown before quiz starts
//   - Dynamic difficulty: re-arranges easy → hard on retry
//     based on initial performance per category
//   - Star reward system:
//       ★★★ = 100%  → Golden Buzzer + Certification Exam unlocked
//       ★★  = 90%+  → Certification Exam unlocked
//       ★   = 80%+  → Certification Exam unlocked
//       ✗   = <80%  → must retake (dynamically reordered)
//   - Anti-cheat engine:
//       • Tab/window visibility detection
//       • Browser focus loss detection
//       • Right-click disabled during quiz
//       • Copy/paste keyboard shortcuts blocked
//       • DevTools size-change detection
//       • Violations logged and shown on results screen
//   - Sound synthesis (Web Audio API — no external files):
//       • Correct answer chime
//       • Wrong answer buzz
//       • Timer warning beep (last 60 seconds)
//       • Quiz complete fanfare
//       • Golden buzzer sound (100% score)
//   - Category-level performance breakdown
//   - Leaderboard (localStorage — separate from Java/Python)
//   - Brand colours: #100720 dark, #E5C95F gold
//
// Junior dev notes throughout — explaining every concept,
// hook, and pattern used in this file.
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box, Button, Card, CardContent, Typography,
    LinearProgress, Chip, Divider,
    styled, keyframes,
} from '@mui/material';
import { Link } from 'react-router-dom';

// ── MUI Icons ─────────────────────────────────────────────────
import StarIcon               from '@mui/icons-material/Star';
import StarBorderIcon         from '@mui/icons-material/StarBorder';
import EmojiEventsIcon        from '@mui/icons-material/EmojiEvents';
import WarningAmberIcon       from '@mui/icons-material/WarningAmber';
import CheckCircleIcon        from '@mui/icons-material/CheckCircle';
import CancelIcon             from '@mui/icons-material/Cancel';
import TimerIcon              from '@mui/icons-material/Timer';
import SchoolIcon             from '@mui/icons-material/School';
import SecurityIcon           from '@mui/icons-material/Security';
import PlayArrowIcon          from '@mui/icons-material/PlayArrow';
import ReplayIcon             from '@mui/icons-material/Replay';
import ArrowForwardIcon       from '@mui/icons-material/ArrowForward';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

// ============================================================
//  BRAND CONSTANTS
//  Same palette as Header, Vision, Mission, Java/Python quizzes.
//  One place to update — changes everywhere.
// ============================================================
const BRAND = {
    dark:       '#100720',
    darkCard:   '#1a0f35',
    darkBorder: 'rgba(229,201,95,0.2)',
    gold:       '#E5C95F',
    goldenrod:  '#DAA520',
    goldFaint:  'rgba(229,201,95,0.08)',
    white:      '#FFFFFF',
    dimWhite:   'rgba(255,255,255,0.65)',
    success:    '#3ECF8E',
    error:      '#FF6B6B',
    warning:    '#FF9F43',
};

// ============================================================
//  QUIZ CONFIGURATION
//  All behaviour tuning in one place.
//  leaderboardKey is unique — scores don't mix with Java/Python.
// ============================================================
const QUIZ_CONFIG = {
    totalTimeSeconds: 1200,   // 20 minutes
    warningThreshold: 60,     // Seconds left when timer turns red
    passMark:         0.80,   // 80% = 1 star minimum
    twoStarMark:      0.90,   // 90% = 2 stars
    threeStarMark:    1.00,   // 100% = 3 stars + Golden Buzzer
    maxViolations:    3,      // Auto-terminate after 3 violations
    leaderboardKey:   'javascript_quiz_leaderboard',
};

// ============================================================
//  JAVASCRIPT QUESTION BANK
//
//  13 categories × 3 questions = 39 questions total.
//  difficulty: 'easy' | 'medium' | 'hard'
//  Used by reorderByDifficulty() on retry to adapt question order.
//
//  Junior dev note: All content is plain JS objects in an array.
//  Keeping data separate from UI makes it easy to swap in a
//  backend API later — just replace this array with a fetch() call.
// ============================================================
const jsQuestionBank = [

    // ── Syntax & Basics ───────────────────────────────────────
    {
        question: 'Which keyword is used to declare a variable that cannot be reassigned?',
        options: ['var', 'let', 'const', 'static'],
        answer: 'const',
        explanation: '`const` declares a block-scoped variable that cannot be reassigned after initialisation.',
        link: '/js-syntax',
        category: 'Syntax & Basics',
        difficulty: 'easy',
    },
    {
        question: 'Which symbol is used for single-line comments in JavaScript?',
        options: ['#', '--', '//', '/*'],
        answer: '//',
        explanation: '`//` starts a single-line comment. `/* */` is used for multi-line comments.',
        link: '/js-syntax',
        category: 'Syntax & Basics',
        difficulty: 'easy',
    },
    {
        question: 'What does the `typeof` operator return for a function?',
        options: ["'object'", "'function'", "'callable'", "'method'"],
        answer: "'function'",
        explanation: '`typeof` returns `"function"` for function values in JavaScript.',
        link: '/js-syntax',
        category: 'Syntax & Basics',
        difficulty: 'medium',
    },

    // ── Variables & Scope ─────────────────────────────────────
    {
        question: 'What is the scope of a variable declared with `var`?',
        options: ['Block scope', 'Function scope', 'Module scope', 'Global scope only'],
        answer: 'Function scope',
        explanation: '`var` is function-scoped — it is accessible anywhere within the function it is declared in.',
        link: '/js-variables',
        category: 'Variables & Scope',
        difficulty: 'medium',
    },
    {
        question: 'What is hoisting in JavaScript?',
        options: [
            'Moving code to the bottom of the file',
            'Declarations being moved to the top of their scope before execution',
            'Copying variables between functions',
            'Importing modules at the top',
        ],
        answer: 'Declarations being moved to the top of their scope before execution',
        explanation: 'JavaScript hoists `var` declarations (not initialisations) and function declarations to the top of their scope.',
        link: '/js-variables',
        category: 'Variables & Scope',
        difficulty: 'medium',
    },
    {
        question: 'What is the Temporal Dead Zone (TDZ)?',
        options: [
            'A timeout error in async code',
            'The period between entering scope and initialisation of let/const',
            'An undefined variable in global scope',
            'A deleted variable in memory',
        ],
        answer: 'The period between entering scope and initialisation of let/const',
        explanation: 'Accessing a `let` or `const` variable before its declaration throws a ReferenceError due to the TDZ.',
        link: '/js-variables',
        category: 'Variables & Scope',
        difficulty: 'hard',
    },

    // ── Data Types ────────────────────────────────────────────
    {
        question: 'Which of the following is NOT a primitive data type in JavaScript?',
        options: ['string', 'number', 'object', 'boolean'],
        answer: 'object',
        explanation: 'Objects are reference types, not primitives. Primitives include string, number, boolean, null, undefined, symbol, and bigint.',
        link: '/js-datatypes',
        category: 'Data Types',
        difficulty: 'medium',
    },
    {
        question: 'What does `typeof null` return in JavaScript?',
        options: ["'null'", "'undefined'", "'object'", "'boolean'"],
        answer: "'object'",
        explanation: '`typeof null === "object"` is a well-known JavaScript bug that has existed since the first version.',
        link: '/js-datatypes',
        category: 'Data Types',
        difficulty: 'hard',
    },
    {
        question: 'What is the result of 0.1 + 0.2 === 0.3 in JavaScript?',
        options: ['true', 'false', 'undefined', 'NaN'],
        answer: 'false',
        explanation: 'Floating-point arithmetic is imprecise. 0.1 + 0.2 equals 0.30000000000000004, not 0.3.',
        link: '/js-datatypes',
        category: 'Data Types',
        difficulty: 'hard',
    },

    // ── Functions ─────────────────────────────────────────────
    {
        question: 'What is an arrow function in JavaScript?',
        options: [
            'A function that returns an object',
            'A shorter syntax for functions that does not bind its own `this`',
            'A function stored in an array',
            'A recursive function',
        ],
        answer: 'A shorter syntax for functions that does not bind its own `this`',
        explanation: 'Arrow functions use `=>` syntax and inherit `this` from the enclosing scope rather than defining their own.',
        link: '/js-functions',
        category: 'Functions',
        difficulty: 'medium',
    },
    {
        question: 'What is a higher-order function?',
        options: [
            'A function with more than 5 parameters',
            'A function that takes or returns another function',
            'A function declared at the top of a file',
            'A function that runs in a Worker thread',
        ],
        answer: 'A function that takes or returns another function',
        explanation: 'Higher-order functions accept functions as arguments or return them. Examples: map, filter, reduce.',
        link: '/js-functions',
        category: 'Functions',
        difficulty: 'medium',
    },
    {
        question: 'What does the `arguments` object contain?',
        options: [
            'Only named parameters',
            'All arguments passed to a regular function',
            'Arguments passed to arrow functions',
            'Global variables',
        ],
        answer: 'All arguments passed to a regular function',
        explanation: '`arguments` is an array-like object available inside regular functions containing all passed arguments. Not available in arrow functions.',
        link: '/js-functions',
        category: 'Functions',
        difficulty: 'hard',
    },

    // ── Arrays ────────────────────────────────────────────────
    {
        question: 'Which array method creates a new array with transformed elements?',
        options: ['forEach()', 'filter()', 'map()', 'reduce()'],
        answer: 'map()',
        explanation: '`map()` applies a function to each element and returns a NEW array with the results.',
        link: '/js-arrays',
        category: 'Arrays',
        difficulty: 'easy',
    },
    {
        question: 'What does the `spread` operator do with arrays?',
        options: [
            'Removes duplicate elements',
            'Expands an array into individual elements',
            'Sorts the array',
            'Flattens nested arrays',
        ],
        answer: 'Expands an array into individual elements',
        explanation: '`...arr` spreads array items. Useful for copying: `[...a, ...b]` or passing to functions: `Math.max(...nums)`.',
        link: '/js-arrays',
        category: 'Arrays',
        difficulty: 'medium',
    },
    {
        question: 'Which method removes and returns the last element of an array?',
        options: ['shift()', 'unshift()', 'pop()', 'splice()'],
        answer: 'pop()',
        explanation: '`pop()` removes the last element and returns it. `shift()` removes the first element.',
        link: '/js-arrays',
        category: 'Arrays',
        difficulty: 'easy',
    },

    // ── Objects ───────────────────────────────────────────────
    {
        question: 'What does object destructuring do?',
        options: [
            'Deletes an object',
            'Copies an object by reference',
            'Extracts properties into individual variables',
            'Converts an object to an array',
        ],
        answer: 'Extracts properties into individual variables',
        explanation: '`const { name, age } = person` extracts `name` and `age` into their own variables.',
        link: '/js-objects',
        category: 'Objects',
        difficulty: 'medium',
    },
    {
        question: 'What does Object.keys() return?',
        options: [
            'An array of the object\'s values',
            'An array of the object\'s own enumerable property names',
            'A copy of the object',
            'The prototype chain',
        ],
        answer: 'An array of the object\'s own enumerable property names',
        explanation: '`Object.keys(obj)` returns an array of the object\'s own (not inherited) property names.',
        link: '/js-objects',
        category: 'Objects',
        difficulty: 'medium',
    },
    {
        question: 'What is the difference between == and === in JavaScript?',
        options: [
            'No difference',
            '== checks value only; === checks value AND type',
            '=== checks value only; == checks value AND type',
            '== is for numbers; === is for strings',
        ],
        answer: '== checks value only; === checks value AND type',
        explanation: '`==` performs type coercion. `===` (strict equality) requires both value and type to match.',
        link: '/js-objects',
        category: 'Objects',
        difficulty: 'easy',
    },

    // ── DOM Manipulation ──────────────────────────────────────
    {
        question: 'Which method selects an element by its ID?',
        options: [
            'document.querySelector()',
            'document.getElementById()',
            'document.getElement()',
            'document.selectId()',
        ],
        answer: 'document.getElementById()',
        explanation: '`getElementById()` returns the element with the matching id attribute.',
        link: '/js-dom',
        category: 'DOM Manipulation',
        difficulty: 'easy',
    },
    {
        question: 'Which method adds a new child node to a parent element?',
        options: ['insertNode()', 'addChild()', 'appendChild()', 'pushElement()'],
        answer: 'appendChild()',
        explanation: '`parent.appendChild(child)` adds `child` as the last child of `parent`.',
        link: '/js-dom',
        category: 'DOM Manipulation',
        difficulty: 'easy',
    },
    {
        question: 'What does event.preventDefault() do?',
        options: [
            'Stops the event from firing',
            'Removes the event listener',
            'Prevents the browser\'s default action for the event',
            'Stops event propagation',
        ],
        answer: 'Prevents the browser\'s default action for the event',
        explanation: 'For example, calling it on a form submit event prevents the page from reloading.',
        link: '/js-dom',
        category: 'DOM Manipulation',
        difficulty: 'medium',
    },

    // ── ES6+ Features ─────────────────────────────────────────
    {
        question: 'What is a template literal in JavaScript?',
        options: [
            'A string enclosed in single quotes',
            'A string using backticks that supports embedded expressions',
            'A constant string value',
            'A string imported from a module',
        ],
        answer: 'A string using backticks that supports embedded expressions',
        explanation: 'Template literals use backticks (`) and allow `${expression}` interpolation and multiline strings.',
        link: '/js-es6',
        category: 'ES6+ Features',
        difficulty: 'easy',
    },
    {
        question: 'What does the optional chaining operator (?.) do?',
        options: [
            'Creates a new object property',
            'Throws an error if the property is null',
            'Safely accesses nested properties without throwing if null or undefined',
            'Assigns a default value',
        ],
        answer: 'Safely accesses nested properties without throwing if null or undefined',
        explanation: '`user?.address?.city` returns undefined if any part of the chain is null/undefined, instead of throwing.',
        link: '/js-es6',
        category: 'ES6+ Features',
        difficulty: 'medium',
    },
    {
        question: 'What does the nullish coalescing operator (??) return?',
        options: [
            'The left side if it is falsy',
            'The right side if the left side is null or undefined',
            'The right side if the left side is false',
            'A boolean',
        ],
        answer: 'The right side if the left side is null or undefined',
        explanation: '`a ?? b` returns `b` only when `a` is null or undefined. Unlike `||`, it does not trigger on 0 or empty string.',
        link: '/js-es6',
        category: 'ES6+ Features',
        difficulty: 'hard',
    },

    // ── Promises & Async ──────────────────────────────────────
    {
        question: 'What are the three states of a JavaScript Promise?',
        options: [
            'waiting, done, failed',
            'pending, fulfilled, rejected',
            'loading, success, error',
            'start, running, complete',
        ],
        answer: 'pending, fulfilled, rejected',
        explanation: 'A Promise starts as `pending`, then settles as either `fulfilled` (resolved) or `rejected` (failed).',
        link: '/js-async',
        category: 'Promises & Async',
        difficulty: 'medium',
    },
    {
        question: 'What does the `await` keyword do?',
        options: [
            'Creates a new thread',
            'Pauses execution of an async function until the Promise settles',
            'Converts a callback to a Promise',
            'Cancels a Promise',
        ],
        answer: 'Pauses execution of an async function until the Promise settles',
        explanation: '`await` can only be used inside `async` functions. It pauses that function until the awaited Promise resolves or rejects.',
        link: '/js-async',
        category: 'Promises & Async',
        difficulty: 'medium',
    },
    {
        question: 'What does Promise.all() do?',
        options: [
            'Runs promises one after another',
            'Returns the first promise to settle',
            'Waits for all promises and rejects if any one rejects',
            'Ignores rejected promises',
        ],
        answer: 'Waits for all promises and rejects if any one rejects',
        explanation: '`Promise.all([p1, p2])` resolves when all promises resolve, or rejects immediately if any one rejects.',
        link: '/js-async',
        category: 'Promises & Async',
        difficulty: 'hard',
    },

    // ── Error Handling ────────────────────────────────────────
    {
        question: 'Which statement is used to handle errors in JavaScript?',
        options: ['if/else', 'try/catch', 'switch/case', 'do/while'],
        answer: 'try/catch',
        explanation: '`try` contains code that might throw. `catch` receives the error object and handles it.',
        link: '/js-errors',
        category: 'Error Handling',
        difficulty: 'easy',
    },
    {
        question: 'How do you throw a custom error in JavaScript?',
        options: [
            'error("message")',
            'raise new Error("message")',
            'throw new Error("message")',
            'reject("message")',
        ],
        answer: 'throw new Error("message")',
        explanation: '`throw` is the keyword. `new Error("message")` creates an Error object with a message property.',
        link: '/js-errors',
        category: 'Error Handling',
        difficulty: 'easy',
    },
    {
        question: 'What does the `finally` block do in JavaScript?',
        options: [
            'Only runs if no error is thrown',
            'Only runs if an error is thrown',
            'Runs after try/catch regardless of whether an error occurred',
            'Catches unhandled promise rejections',
        ],
        answer: 'Runs after try/catch regardless of whether an error occurred',
        explanation: '`finally` always runs — useful for cleanup like closing connections or hiding loading spinners.',
        link: '/js-errors',
        category: 'Error Handling',
        difficulty: 'medium',
    },

    // ── Closures ──────────────────────────────────────────────
    {
        question: 'What is a closure in JavaScript?',
        options: [
            'A function that has no return value',
            'A function that remembers variables from its outer scope even after that scope has closed',
            'A way to close the browser window',
            'A method to end a loop',
        ],
        answer: 'A function that remembers variables from its outer scope even after that scope has closed',
        explanation: 'Closures allow inner functions to access outer function variables even after the outer function has returned.',
        link: '/js-closures',
        category: 'Closures',
        difficulty: 'hard',
    },
    {
        question: 'What is a common use case for closures?',
        options: [
            'Defining global variables',
            'Creating private variables and data encapsulation',
            'Importing modules',
            'Setting timeouts',
        ],
        answer: 'Creating private variables and data encapsulation',
        explanation: 'Closures enable data privacy — variables inside a closure are inaccessible from outside the function.',
        link: '/js-closures',
        category: 'Closures',
        difficulty: 'hard',
    },
    {
        question: 'What will the following log: for(var i=0; i<3; i++) { setTimeout(() => console.log(i), 0); }',
        options: ['0 1 2', '1 2 3', '3 3 3', '0 0 0'],
        answer: '3 3 3',
        explanation: 'var is function-scoped, so all three callbacks share the same `i`. By the time they run, the loop has finished and i is 3.',
        link: '/js-closures',
        category: 'Closures',
        difficulty: 'hard',
    },

    // ── Prototypes & OOP ──────────────────────────────────────
    {
        question: 'Which keyword is used to define a class in JavaScript?',
        options: ['object', 'class', 'Class', 'prototype'],
        answer: 'class',
        explanation: 'ES6 introduced `class` syntax. It is syntactic sugar over JavaScript\'s prototype-based inheritance.',
        link: '/js-oop',
        category: 'Prototypes & OOP',
        difficulty: 'easy',
    },
    {
        question: 'What does the `extends` keyword do in JavaScript?',
        options: [
            'Adds new methods to an object',
            'Copies all properties from one object to another',
            'Creates a subclass that inherits from a parent class',
            'Imports a class from another file',
        ],
        answer: 'Creates a subclass that inherits from a parent class',
        explanation: '`class Dog extends Animal` makes Dog inherit all methods and properties from Animal.',
        link: '/js-oop',
        category: 'Prototypes & OOP',
        difficulty: 'medium',
    },
    {
        question: 'What is the prototype chain?',
        options: [
            'A list of all functions in a file',
            'The chain of objects JavaScript searches for a property',
            'A series of nested callbacks',
            'An array of class instances',
        ],
        answer: 'The chain of objects JavaScript searches for a property',
        explanation: 'When a property is not found on an object, JS looks up the prototype chain until it finds it or reaches null.',
        link: '/js-oop',
        category: 'Prototypes & OOP',
        difficulty: 'hard',
    },

    // ── Modules ───────────────────────────────────────────────
    {
        question: 'Which keyword is used to export a value from an ES module?',
        options: ['module.exports', 'export', 'send', 'share'],
        answer: 'export',
        explanation: 'ES modules use `export` to expose values and `import` to consume them.',
        link: '/js-modules',
        category: 'Modules',
        difficulty: 'easy',
    },
    {
        question: 'What is the difference between default and named exports?',
        options: [
            'No difference',
            'Default exports can only be imported with the same name',
            'Named exports use curly braces; default exports do not',
            'Default exports are only for functions',
        ],
        answer: 'Named exports use curly braces; default exports do not',
        explanation: 'Named: `import { add } from "./math"`. Default: `import add from "./math"`. A module can have many named exports but only one default.',
        link: '/js-modules',
        category: 'Modules',
        difficulty: 'medium',
    },
    {
        question: 'What does `import * as utils from "./utils"` do?',
        options: [
            'Imports only the default export',
            'Imports all named exports into an object called utils',
            'Imports the file as a string',
            'Runs the utils file immediately',
        ],
        answer: 'Imports all named exports into an object called utils',
        explanation: '`* as utils` creates a namespace object. You then access exports as `utils.funcName()`.',
        link: '/js-modules',
        category: 'Modules',
        difficulty: 'medium',
    },
];

// ============================================================
//  UTILITY FUNCTIONS
//  Pure functions — no side effects, reusable and testable.
// ============================================================

/**
 * shuffle
 * Returns a new randomly ordered copy of the input array.
 * Uses spread [...arr] to avoid mutating the original.
 */
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

/**
 * getStars
 * Maps score/total ratio to 0–3 stars using QUIZ_CONFIG thresholds.
 */
const getStars = (score, total) => {
    const ratio = score / total;
    if (ratio >= QUIZ_CONFIG.threeStarMark) return 3;
    if (ratio >= QUIZ_CONFIG.twoStarMark)   return 2;
    if (ratio >= QUIZ_CONFIG.passMark)       return 1;
    return 0;
};

/**
 * getBadge
 * Returns a JavaScript-themed badge label for results and leaderboard.
 */
const getBadge = (stars) => {
    if (stars === 3) return '🌟 JS Wizard';
    if (stars === 2) return '⚡ JS Pro';
    if (stars === 1) return '📘 JS Apprentice';
    return '🌱 JS Novice';
};

/**
 * formatTime
 * Converts raw seconds to MM:SS display string.
 * padStart(2, '0') — ensures "05" not "5".
 */
const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

/**
 * saveScore / loadScores
 * Persist top 5 quiz results to localStorage under the JS key.
 * Separate key from Java and Python quizzes so scores don't mix.
 */
const saveScore = (entry) => {
    const prev    = JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');
    const updated = [entry, ...prev].slice(0, 5);
    localStorage.setItem(QUIZ_CONFIG.leaderboardKey, JSON.stringify(updated));
};

const loadScores = () =>
    JSON.parse(localStorage.getItem(QUIZ_CONFIG.leaderboardKey) || '[]');

/**
 * reorderByDifficulty
 * Adaptive retry — categories with low accuracy appear last.
 * Higher accuracy ratio = user is stronger = shown first (easy warmup).
 */
const reorderByDifficulty = (questions, categoryScores) => {
    return [...questions].sort((a, b) => {
        const aRatio = categoryScores[a.category]
            ? categoryScores[a.category].correct / categoryScores[a.category].total
            : 0.5;
        const bRatio = categoryScores[b.category]
            ? categoryScores[b.category].correct / categoryScores[b.category].total
            : 0.5;
        return bRatio - aRatio;
    });
};

// ============================================================
//  SOUND ENGINE — Web Audio API
//
//  Synthesised entirely in the browser — zero audio files needed.
//  audioCtx is lazily created on first user interaction to comply
//  with browser autoplay policies (no sound before a click).
// ============================================================

let audioCtx = null;

/**
 * getAudioContext
 * Lazy initialisation — creates AudioContext on first call.
 * Bracket notation window['webkitAudioContext'] works in plain JS
 * (no TypeScript `as any` needed here).
 */
const getAudioContext = () => {
    if (!audioCtx) {
        const AudioContextClass = window.AudioContext || window['webkitAudioContext'];
        audioCtx = new AudioContextClass();
    }
    return audioCtx;
};

/**
 * playTone — core audio primitive.
 * Creates oscillator → gain → output destination.
 * Smooth fade in/out prevents audio clicks at note boundaries.
 */
const playTone = (frequency, duration, type = 'sine', volume = 0.3, delay = 0) => {
    try {
        const ctx  = getAudioContext();
        const osc  = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = type;
        osc.frequency.setValueAtTime(frequency, ctx.currentTime + delay);
        gain.gain.setValueAtTime(0, ctx.currentTime + delay);
        gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + delay + 0.02);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + delay + duration);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + duration + 0.05);
    } catch (e) {
        console.warn('Sound playback failed:', e);
    }
};

/** Correct — ascending two-note chime */
const playCorrect = () => {
    playTone(523, 0.15, 'sine', 0.3, 0);
    playTone(659, 0.2,  'sine', 0.3, 0.12);
};

/** Wrong — descending square buzz */
const playWrong = () => {
    playTone(300, 0.1,  'square', 0.2, 0);
    playTone(200, 0.15, 'square', 0.2, 0.12);
};

/** Warning beep — plays every 10s in last 60 seconds */
const playWarningBeep = () => playTone(880, 0.08, 'sine', 0.25, 0);

/** Complete fanfare — ascending 4-note arpeggio */
const playComplete = () =>
    [523, 659, 784, 1047].forEach((f, i) => playTone(f, 0.25, 'sine', 0.35, i * 0.12));

/** Golden Buzzer — triumphant chord + rising arpeggio (100% only) */
const playGoldenBuzzer = () => {
    [523, 659, 784].forEach((f) => playTone(f, 0.6, 'sine', 0.25, 0));
    [523, 659, 784, 1047, 1319].forEach((f, i) =>
        playTone(f, 0.3, 'triangle', 0.3, 0.5 + i * 0.1)
    );
};

/**
 * speakText
 * Web Speech API — browser built-in text-to-speech.
 * No API key or library required. Silent fallback if unsupported.
 */
const speakText = (text, rate = 0.9) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utter  = new SpeechSynthesisUtterance(text);
    utter.rate   = rate;
    utter.pitch  = 1.1;
    utter.volume = 1;
    window.speechSynthesis.speak(utter);
};

// ============================================================
//  STYLED COMPONENTS
//  Identical visual language to Java/Python quizzes.
// ============================================================

/** pulseGold — glowing pulse on Begin button and Golden Buzzer */
const pulseGold = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(229,201,95,0.4); }
    50%       { box-shadow: 0 0 0 20px rgba(229,201,95,0); }
`;

/** shakeAnim — horizontal shake for wrong answers and violations */
const shakeAnim = keyframes`
    0%, 100% { transform: translateX(0); }
    20%       { transform: translateX(-8px); }
    40%       { transform: translateX(8px); }
    60%       { transform: translateX(-6px); }
    80%       { transform: translateX(6px); }
`;

const PageWrapper = styled(Box)({
    minHeight:       '100vh',
    backgroundColor: BRAND.dark,
    fontFamily:      "'Poppins', sans-serif",
    padding:         '24px 16px 48px',
    overflowX:       'hidden',
});

const QuizCard = styled(Card)({
    backgroundColor: BRAND.darkCard,
    border:          `1px solid ${BRAND.darkBorder}`,
    borderRadius:    '20px',
    backdropFilter:  'blur(10px)',
    maxWidth:        '860px',
    margin:          '0 auto',
    overflow:        'visible',
});

const GoldButton = styled(Button)({
    background:    `linear-gradient(135deg, ${BRAND.gold}, ${BRAND.goldenrod})`,
    color:         BRAND.dark,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    800,
    fontSize:      '0.9rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding:       '12px 28px',
    borderRadius:  '10px',
    transition:    'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        background: `linear-gradient(135deg, ${BRAND.goldenrod}, ${BRAND.gold})`,
        transform:  'translateY(-2px)',
        boxShadow:  '0 8px 20px rgba(229,201,95,0.3)',
    },
    '&.Mui-disabled': {
        background: 'rgba(255,255,255,0.1)',
        color:      'rgba(255,255,255,0.3)',
    },
});

const OutlineButton = styled(Button)({
    borderColor:   BRAND.gold,
    color:         BRAND.gold,
    fontFamily:    "'Poppins', sans-serif",
    fontWeight:    700,
    borderRadius:  '10px',
    padding:       '10px 24px',
    transition:    'background 0.2s',
    '&:hover': {
        backgroundColor: BRAND.goldFaint,
        borderColor:     BRAND.goldenrod,
    },
});

const ViolationBanner = styled(Box)({
    backgroundColor: 'rgba(255,107,107,0.15)',
    border:          '1px solid rgba(255,107,107,0.4)',
    borderRadius:    '10px',
    padding:         '10px 16px',
    display:         'flex',
    alignItems:      'center',
    gap:             '10px',
    marginBottom:    '16px',
    animation:       `${shakeAnim} 0.4s ease`,
});

// ============================================================
//  STAR DISPLAY COMPONENT
// ============================================================
const StarDisplay = ({ stars, size = 'large' }) => {
    const fontSize = size === 'large' ? '2.5rem' : '1.2rem';
    return (
        <Box display="flex" gap={0.5} justifyContent="center">
            {[1, 2, 3].map((i) =>
                i <= stars
                    ? <StarIcon key={i} sx={{ fontSize, color: BRAND.gold, filter: 'drop-shadow(0 0 4px rgba(229,201,95,0.6))' }} />
                    : <StarBorderIcon key={i} sx={{ fontSize, color: 'rgba(255,255,255,0.2)' }} />
            )}
        </Box>
    );
};

// ============================================================
//  MAIN COMPONENT — JavaScriptQuiz
//
//  Add to App.js:
//    import JavaScriptQuiz from "./pages/Languages/JavaScript/JavaScriptQuiz";
//    <Route path="/javascript-quiz" element={<JavaScriptQuiz />} />
// ============================================================
function JavaScriptQuiz() {

    // ── Phase & Identity ──────────────────────────────────────
    // 4 phases: 'welcome' → 'rules' → 'quiz' → results (finished=true)
    const [phase,      setPhase]     = useState('welcome');
    const [userName,   setUserName]  = useState('');
    const [nameInput,  setNameInput] = useState('');

    // ── Quiz State ────────────────────────────────────────────
    const [questions,   setQuestions]   = useState([]);
    const [current,     setCurrent]     = useState(0);
    const [score,       setScore]       = useState(0);
    const [selected,    setSelected]    = useState(null);
    const [showAnswer,  setShowAnswer]  = useState(false);
    const [userAnswers, setUserAnswers] = useState([]);
    const [isRetry,     setIsRetry]     = useState(false);

    // Stores per-category accuracy from last attempt for adaptive reorder
    const [prevCategoryScores, setPrevCategoryScores] = useState({});

    // ── Timer ─────────────────────────────────────────────────
    const [timer,    setTimer]    = useState(QUIZ_CONFIG.totalTimeSeconds);
    const [finished, setFinished] = useState(false);

    // ── Anti-Cheat ────────────────────────────────────────────
    const [violations,    setViolations]    = useState(0);
    const [violationLog,  setViolationLog]  = useState([]);
    const [showViolation, setShowViolation] = useState(false);
    const [lastViolation, setLastViolation] = useState('');

    // ── Refs ──────────────────────────────────────────────────
    // Junior dev note: Refs are used here because event listener
    // callbacks close over initial state values (stale closure problem).
    // violationCountRef always gives us the real current count inside
    // event handlers, whereas `violations` state would be stale.
    const violationCountRef = useRef(0);
    const quizActiveRef     = useRef(false);
    const warningPlayedRef  = useRef(false);

    // ============================================================
    //  QUESTION INITIALISATION
    // ============================================================
    const initQuestions = useCallback((retry, catScores) => {
        let bank = jsQuestionBank.map(q => ({ ...q, options: shuffle(q.options) }));
        if (retry && Object.keys(catScores).length > 0) {
            bank = reorderByDifficulty(bank, catScores);
        } else {
            bank = shuffle(bank);
        }
        setQuestions(bank);
    }, []);

    // ============================================================
    //  ANTI-CHEAT ENGINE
    //  Attaches event listeners when phase === 'quiz'.
    //  Returns cleanup function to remove them when done.
    // ============================================================
    const recordViolation = useCallback((message) => {
        if (!quizActiveRef.current) return;

        violationCountRef.current += 1;
        const count = violationCountRef.current;

        setViolations(count);
        setViolationLog(prev => [...prev, `[${new Date().toLocaleTimeString()}] ${message}`]);
        setLastViolation(message);
        setShowViolation(true);

        playTone(440, 0.3, 'square', 0.3, 0);
        speakText('Violation detected. ' + message);
        setTimeout(() => setShowViolation(false), 4000);

        if (count >= QUIZ_CONFIG.maxViolations) {
            speakText('Maximum violations reached. Quiz terminated.');
            quizActiveRef.current = false;
            setFinished(true);
        }
    }, []);

    useEffect(() => {
        if (phase !== 'quiz') return;

        const handleVisibility  = () => { if (document.hidden) recordViolation('Tab switch or window minimised detected.'); };
        const handleBlur        = ()  => recordViolation('Browser window lost focus.');
        const handleContextMenu = (e) => { e.preventDefault(); recordViolation('Right-click attempt blocked.'); };
        const handleKeyDown     = (e) => {
            const blocked =
                (e.ctrlKey && ['c', 'v', 'u', 's', 'a', 'p'].includes(e.key.toLowerCase())) ||
                e.key === 'F12' ||
                (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase()));
            if (blocked) {
                e.preventDefault();
                recordViolation(`Blocked shortcut: ${e.ctrlKey ? 'Ctrl+' : ''}${e.shiftKey ? 'Shift+' : ''}${e.key}`);
            }
        };
        const handleResize = () => {
            if (window.outerHeight - window.innerHeight > 160 || window.outerWidth - window.innerWidth > 160) {
                recordViolation('Possible DevTools opened — browser size change detected.');
            }
        };

        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('blur',          handleBlur);
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown',     handleKeyDown);
        window.addEventListener('resize',        handleResize);
        quizActiveRef.current = true;

        return () => {
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('blur',          handleBlur);
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown',     handleKeyDown);
            window.removeEventListener('resize',        handleResize);
            quizActiveRef.current = false;
        };
    }, [phase, recordViolation]);

    // ============================================================
    //  COUNTDOWN TIMER
    // ============================================================
    useEffect(() => {
        if (phase !== 'quiz' || finished) return;
        if (timer <= 0) { speakText('Time is up. Quiz finished.'); setFinished(true); return; }
        if (timer <= QUIZ_CONFIG.warningThreshold && timer % 10 === 0) {
            playWarningBeep();
            if (timer === QUIZ_CONFIG.warningThreshold) speakText(`${QUIZ_CONFIG.warningThreshold} seconds remaining.`);
        }
        const id = setTimeout(() => setTimer(t => t - 1), 1000);
        return () => clearTimeout(id);
    }, [timer, finished, phase]);

    // ============================================================
    //  RESULTS TRIGGER
    // ============================================================
    useEffect(() => {
        if (!finished || questions.length === 0) return;
        quizActiveRef.current = false;
        const stars = getStars(score, questions.length);

        if (stars === 3) {
            playGoldenBuzzer();
            speakText(`Congratulations ${userName}! Perfect score! Golden Buzzer and three stars!`);
        } else {
            playComplete();
            speakText(`Well done ${userName}. You scored ${score} out of ${questions.length}. ${stars} star${stars !== 1 ? 's' : ''}.`);
        }

        const catStats = {};
        questions.forEach((q, i) => {
            if (!catStats[q.category]) catStats[q.category] = { correct: 0, total: 0 };
            catStats[q.category].total++;
            if (userAnswers[i] === q.answer) catStats[q.category].correct++;
        });
        setPrevCategoryScores(catStats);

        saveScore({ date: new Date().toLocaleString(), score, total: questions.length, timeRemaining: timer, badge: getBadge(stars), stars, name: userName });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [finished]);

    // ============================================================
    //  EVENT HANDLERS
    // ============================================================
    const handleNameSubmit = () => {
        const trimmed = nameInput.trim();
        if (!trimmed) return;
        setUserName(trimmed);
        speakText(`Welcome ${trimmed}. Please read the rules carefully before you begin.`);
        setPhase('rules');
    };

    const handleStartQuiz = () => {
        initQuestions(isRetry, prevCategoryScores);
        setCurrent(0); setScore(0); setSelected(null);
        setShowAnswer(false); setUserAnswers([]);
        setTimer(QUIZ_CONFIG.totalTimeSeconds); setFinished(false);
        setViolations(0); setViolationLog([]);
        violationCountRef.current = 0;
        warningPlayedRef.current  = false;
        setPhase('quiz');

        speakText(`Are you ready, ${userName}?`, 0.85);
        setTimeout(() => speakText('3', 1.2), 1800);
        setTimeout(() => speakText('2', 1.2), 2800);
        setTimeout(() => speakText('1', 1.2), 3800);
        setTimeout(() => speakText('Begin!', 1.0), 4800);
    };

    const handleAnswer = (opt) => {
        if (showAnswer) return;
        setSelected(opt);
        setUserAnswers(prev => [...prev, opt]);
        setShowAnswer(true);
        if (opt === questions[current].answer) { setScore(s => s + 1); playCorrect(); }
        else { playWrong(); }
    };

    const handleNext = () => {
        if (current + 1 < questions.length) {
            setCurrent(c => c + 1); setShowAnswer(false); setSelected(null);
        } else { setFinished(true); }
    };

    const handleRetry = () => {
        setIsRetry(true);
        setPhase('rules');
        speakText(`Let's try again, ${userName}. Questions are ordered by your weakest areas.`);
    };

    // ============================================================
    //  DERIVED VALUES
    // ============================================================
    const categoryStats = questions.reduce((acc, q, i) => {
        if (!acc[q.category]) acc[q.category] = { correct: 0, total: 0 };
        acc[q.category].total++;
        if (finished && userAnswers[i] === q.answer) acc[q.category].correct++;
        return acc;
    }, {});

    const stars          = finished ? getStars(score, questions.length) : 0;
    const passed         = stars >= 1;
    const isGoldenBuzzer = stars === 3;
    const progressPct    = questions.length > 0 ? (current / questions.length) * 100 : 0;
    const timerWarning   = timer <= QUIZ_CONFIG.warningThreshold;

    // ============================================================
    //  RENDER — WELCOME SCREEN
    // ============================================================
    if (phase === 'welcome') {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={4}>
                            <Typography sx={{ fontSize: '3rem', lineHeight: 1 }}>🌐</Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.4rem' }, color: BRAND.white, letterSpacing: '-0.02em', mt: 1 }}>
                                JavaScript Mastery Quiz
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                ProDev Adaptive Learning Platform
                            </Typography>
                            <Box mt={2}><StarDisplay stars={3} size="large" /></Box>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 4 }} />

                        <Box maxWidth={420} mx="auto">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '1rem', color: BRAND.white, mb: 0.5 }}>
                                Enter your full name to begin
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                Your name will appear on your certificate and leaderboard entry.
                            </Typography>

                            <Box
                                component="input"
                                placeholder="e.g. Kwame Asante"
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleNameSubmit()}
                                sx={{
                                    width: '100%', p: '14px 18px', borderRadius: '10px',
                                    border: `1px solid ${BRAND.darkBorder}`,
                                    backgroundColor: 'rgba(255,255,255,0.05)',
                                    color: BRAND.white, fontFamily: "'Poppins', sans-serif",
                                    fontSize: '1rem', outline: 'none', mb: 3, boxSizing: 'border-box',
                                    '&:focus':        { borderColor: BRAND.gold },
                                    '&::placeholder': { color: 'rgba(255,255,255,0.3)' },
                                }}
                            />

                            <GoldButton fullWidth onClick={handleNameSubmit} disabled={!nameInput.trim()} endIcon={<ArrowForwardIcon />}>
                                Continue to Rules
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RULES SCREEN
    // ============================================================
    if (phase === 'rules') {
        const rules = [
            { icon: <TimerIcon />,               title: 'Time Limit',         text: `You have ${QUIZ_CONFIG.totalTimeSeconds / 60} minutes to answer all ${jsQuestionBank.length} questions. Timer starts immediately when you click Begin.` },
            { icon: <StarIcon />,                 title: 'Star Rewards',       text: 'Score 80%+ for ★, 90%+ for ★★, 100% for ★★★ (Golden Buzzer!). Earn at least ★ to unlock the Final Certification Exam.' },
            { icon: <ReplayIcon />,               title: 'Failed Attempts',    text: 'Score below 80% and you must retake. Questions reorder based on your weakest categories — easiest first.' },
            { icon: <SecurityIcon />,             title: 'Anti-Cheat Rules',   text: `No tab switching, minimising, DevTools, right-clicking, or Ctrl+C/V. ${QUIZ_CONFIG.maxViolations} violations auto-terminate your quiz.` },
            { icon: <NotificationsActiveIcon />,  title: 'Audio Enabled',      text: 'Audio feedback and speech synthesis are active. Keep your volume on. A countdown will play before the first question.' },
            { icon: <SchoolIcon />,               title: 'Academic Integrity', text: 'Do not search for answers online. This quiz tests YOUR JavaScript knowledge. Your certificate only means something if you earn it honestly.' },
        ];

        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>
                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.6rem', md: '2rem' }, color: BRAND.gold }}>
                                Welcome, {userName}! 👋
                            </Typography>
                            <Typography sx={{ color: BRAND.dimWhite, mt: 0.5, fontFamily: "'Poppins', sans-serif", fontSize: '0.88rem' }}>
                                {isRetry ? 'Retaking with adaptive difficulty — weak categories come last.' : 'Read the rules carefully before you begin.'}
                            </Typography>
                        </Box>

                        <Divider sx={{ borderColor: BRAND.darkBorder, mb: 3 }} />

                        {rules.map((rule, i) => (
                            <Box key={i} display="flex" gap={2} alignItems="flex-start" mb={2}
                                 sx={{ p: 2, backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '12px' }}
                            >
                                <Box sx={{ color: BRAND.gold, mt: 0.2, flexShrink: 0 }}>{rule.icon}</Box>
                                <Box>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: BRAND.white, mb: 0.3 }}>
                                        {rule.title}
                                    </Typography>
                                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6 }}>
                                        {rule.text}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}

                        <Divider sx={{ borderColor: BRAND.darkBorder, my: 3 }} />

                        <Box textAlign="center">
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', color: BRAND.white, mb: 3 }}>
                                Are you ready, {userName}? A countdown will play before the first question.
                            </Typography>
                            <GoldButton onClick={handleStartQuiz} startIcon={<PlayArrowIcon />}
                                        sx={{ fontSize: '1rem', px: 5, py: 1.5, animation: `${pulseGold} 2s ease-in-out infinite` }}
                            >
                                I'm Ready — Begin!
                            </GoldButton>
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — RESULTS SCREEN
    // ============================================================
    if (finished) {
        return (
            <PageWrapper>
                <QuizCard>
                    <CardContent sx={{ p: { xs: 3, md: 5 } }}>

                        {isGoldenBuzzer && (
                            <Box textAlign="center" sx={{
                                background: 'linear-gradient(135deg, rgba(229,201,95,0.2), rgba(218,165,32,0.1))',
                                border: `2px solid ${BRAND.gold}`, borderRadius: '16px',
                                p: 3, mb: 3, animation: `${pulseGold} 1.5s ease-in-out infinite`,
                            }}>
                                <Typography sx={{ fontSize: '2.5rem' }}>🔔</Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1.5rem', color: BRAND.gold }}>
                                    GOLDEN BUZZER!
                                </Typography>
                                <Typography sx={{ color: BRAND.dimWhite, fontFamily: "'Poppins', sans-serif", fontSize: '0.9rem' }}>
                                    Perfect score! You are a JavaScript master, {userName}!
                                </Typography>
                            </Box>
                        )}

                        <Box textAlign="center" mb={3}>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: BRAND.white, mb: 1 }}>
                                {passed ? '🎉 Well Done,' : '😓 Keep Going,'} {userName}!
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1.1rem', color: BRAND.dimWhite, mb: 2 }}>
                                Score: <Box component="span" sx={{ color: BRAND.gold, fontWeight: 700 }}>{score}</Box> / {questions.length} ({Math.round((score / questions.length) * 100)}%)
                            </Typography>
                            <StarDisplay stars={stars} size="large" />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '1rem', color: BRAND.gold, mt: 1.5, fontWeight: 600 }}>
                                {getBadge(stars)}
                            </Typography>
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mt: 0.5 }}>
                                ⏱ Time remaining: {formatTime(timer)}
                            </Typography>
                        </Box>

                        {passed ? (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(62,207,142,0.08)', border: '1px solid rgba(62,207,142,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <EmojiEventsIcon sx={{ color: BRAND.success, fontSize: 32, mb: 0.5 }} />
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.success, fontSize: '0.95rem', mb: 2 }}>
                                    You are eligible for the JavaScript Certification Exam!
                                </Typography>
                                <GoldButton component={Link} to="/javascript-certification-exam" endIcon={<ArrowForwardIcon />}>
                                    Attempt Certification Exam
                                </GoldButton>
                            </Box>
                        ) : (
                            <Box sx={{ p: 2.5, mb: 3, backgroundColor: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.3)', borderRadius: '12px', textAlign: 'center' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.95rem', mb: 0.5 }}>
                                    You need 80%+ to unlock the JavaScript Certification Exam.
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, mb: 2 }}>
                                    Retake reorders questions — easier topics first based on your weak areas.
                                </Typography>
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake Quiz (Adaptive)
                                </OutlineButton>
                            </Box>
                        )}

                        {violations > 0 && (
                            <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.25)', borderRadius: '12px' }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.error, fontSize: '0.88rem', mb: 1 }}>
                                    ⚠ {violations} Violation{violations > 1 ? 's' : ''} Detected
                                </Typography>
                                {violationLog.map((v, i) => (
                                    <Typography key={i} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.7)', lineHeight: 1.7 }}>
                                        {v}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            Category Breakdown
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {Object.entries(categoryStats).map(([cat, data]) => {
                                const pct      = Math.round((data.correct / data.total) * 100);
                                const barColor = pct >= 80 ? BRAND.success : pct >= 50 ? BRAND.warning : BRAND.error;
                                return (
                                    <Box key={cat} sx={{ p: '10px 16px', backgroundColor: BRAND.goldFaint, border: `1px solid ${BRAND.darkBorder}`, borderRadius: '10px' }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.82rem', color: BRAND.white }}>{cat}</Typography>
                                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: barColor, fontWeight: 700 }}>
                                                {data.correct}/{data.total} ({pct}%)
                                            </Typography>
                                        </Box>
                                        <LinearProgress variant="determinate" value={pct} sx={{
                                            height: 4, borderRadius: 2,
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                            '& .MuiLinearProgress-bar': { background: barColor, borderRadius: 2 },
                                        }} />
                                    </Box>
                                );
                            })}
                        </Box>

                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, color: BRAND.white, mb: 1.5, fontSize: '1rem' }}>
                            🏅 Leaderboard — Top 5
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1} mb={3}>
                            {loadScores().map((entry, idx) => (
                                <Box key={idx} sx={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    p: '10px 16px',
                                    backgroundColor: idx === 0 ? 'rgba(229,201,95,0.1)' : BRAND.goldFaint,
                                    border: `1px solid ${idx === 0 ? BRAND.gold : BRAND.darkBorder}`,
                                    borderRadius: '10px',
                                }}>
                                    <Box>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: idx === 0 ? BRAND.gold : BRAND.white }}>
                                            #{idx + 1} {entry.name}
                                        </Typography>
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.date} • ⏱ {formatTime(entry.timeRemaining)} left
                                        </Typography>
                                    </Box>
                                    <Box textAlign="right">
                                        <StarDisplay stars={entry.stars} size="small" />
                                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.72rem', color: BRAND.dimWhite }}>
                                            {entry.score}/{entry.total}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>

                        <Box display="flex" gap={2} flexWrap="wrap">
                            <OutlineButton component={Link} to="/home" variant="outlined">Back to Home</OutlineButton>
                            {passed && (
                                <OutlineButton onClick={handleRetry} startIcon={<ReplayIcon />} variant="outlined">
                                    Retake for Better Score
                                </OutlineButton>
                            )}
                        </Box>
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    // ============================================================
    //  RENDER — ACTIVE QUIZ SCREEN
    // ============================================================
    if (phase === 'quiz' && questions.length > 0) {
        const q = questions[current];

        return (
            <PageWrapper>
                {/* Sticky header */}
                <Box sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: BRAND.dark, borderBottom: `1px solid ${BRAND.darkBorder}`, py: 1.5, px: 2, mb: 2 }}>
                    <Box maxWidth="860px" mx="auto" display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.88rem', color: BRAND.dimWhite }}>
                            Question <Box component="span" sx={{ color: BRAND.gold }}>{current + 1}</Box> of {questions.length}
                        </Typography>
                        <Chip label={q.category} size="small" sx={{ backgroundColor: BRAND.goldFaint, color: BRAND.gold, border: `1px solid ${BRAND.darkBorder}`, fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.7rem' }} />
                        <Box display="flex" alignItems="center" gap={0.5}>
                            <TimerIcon sx={{ color: timerWarning ? BRAND.error : BRAND.gold, fontSize: '1.1rem' }} />
                            <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 800, fontSize: '1rem', color: timerWarning ? BRAND.error : BRAND.gold, animation: timerWarning ? `${pulseGold} 1s ease-in-out infinite` : 'none' }}>
                                {formatTime(timer)}
                            </Typography>
                        </Box>
                        <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.dimWhite }}>
                            Score: <Box component="span" sx={{ color: BRAND.gold }}>{score}</Box>
                        </Typography>
                    </Box>
                    <Box maxWidth="860px" mx="auto" mt={1}>
                        <LinearProgress variant="determinate" value={progressPct} sx={{
                            height: 4, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.06)',
                            '& .MuiLinearProgress-bar': { background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.goldenrod})`, borderRadius: 2 },
                        }} />
                    </Box>
                </Box>

                {/* Violation banner */}
                {showViolation && (
                    <Box maxWidth="860px" mx="auto" px={2} mb={1}>
                        <ViolationBanner>
                            <WarningAmberIcon sx={{ color: BRAND.error, flexShrink: 0 }} />
                            <Box>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '0.85rem', color: BRAND.error }}>
                                    ⚠ Violation #{violations} Detected
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: 'rgba(255,107,107,0.8)' }}>
                                    {lastViolation} — {QUIZ_CONFIG.maxViolations - violations} more will terminate this quiz.
                                </Typography>
                            </Box>
                        </ViolationBanner>
                    </Box>
                )}

                {/* Question card */}
                <QuizCard>
                    <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                        <Typography sx={{
                            fontFamily: "'Poppins', sans-serif", fontWeight: 700,
                            fontSize: { xs: '1rem', md: '1.15rem' }, color: BRAND.white,
                            mb: 3, lineHeight: 1.6, p: 2.5,
                            backgroundColor: BRAND.goldFaint,
                            borderLeft: `3px solid ${BRAND.gold}`,
                            borderRadius: '0 10px 10px 0',
                        }}>
                            {current + 1}. {q.question}
                        </Typography>

                        <Box>
                            {q.options.map((opt, i) => {
                                const isCorrect = showAnswer && opt === q.answer;
                                const isWrong   = showAnswer && opt === selected && opt !== q.answer;
                                return (
                                    <Button key={i} onClick={() => handleAnswer(opt)} disabled={showAnswer}
                                            startIcon={
                                                isCorrect ? <CheckCircleIcon sx={{ color: BRAND.success }} /> :
                                                    isWrong   ? <CancelIcon      sx={{ color: BRAND.error   }} /> : null
                                            }
                                            sx={{
                                                width: '100%', textAlign: 'left', justifyContent: 'flex-start',
                                                p: '14px 20px', borderRadius: '10px',
                                                fontFamily: "'Poppins', sans-serif", fontWeight: 500, fontSize: '0.9rem',
                                                textTransform: 'none', mb: 1,
                                                backgroundColor: isCorrect ? 'rgba(62,207,142,0.15)' : isWrong ? 'rgba(255,107,107,0.15)' : 'rgba(255,255,255,0.04)',
                                                border: `1px solid ${isCorrect ? BRAND.success : isWrong ? BRAND.error : 'rgba(255,255,255,0.12)'}`,
                                                color: isCorrect ? BRAND.success : isWrong ? BRAND.error : BRAND.white,
                                                transition: 'all 0.25s ease',
                                                animation: isWrong ? `${shakeAnim} 0.4s ease` : 'none',
                                                '&:hover': !showAnswer ? { backgroundColor: 'rgba(229,201,95,0.08)', borderColor: BRAND.gold } : {},
                                                '&.Mui-disabled': { opacity: 1 },
                                            }}
                                    >
                                        {opt}
                                    </Button>
                                );
                            })}
                        </Box>

                        {showAnswer && (
                            <Box sx={{
                                mt: 2, p: 2,
                                backgroundColor: selected === q.answer ? 'rgba(62,207,142,0.06)' : 'rgba(255,107,107,0.06)',
                                border: `1px solid ${selected === q.answer ? 'rgba(62,207,142,0.3)' : 'rgba(255,107,107,0.3)'}`,
                                borderRadius: '10px',
                            }}>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.85rem', color: selected === q.answer ? BRAND.success : BRAND.error, mb: 0.5 }}>
                                    {selected === q.answer ? '✅ Correct!' : `❌ Incorrect — Correct answer: ${q.answer}`}
                                </Typography>
                                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.82rem', color: BRAND.dimWhite, lineHeight: 1.6, fontStyle: 'italic' }}>
                                    {q.explanation}
                                </Typography>
                                <Typography component={Link} to={q.link} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: '0.78rem', color: BRAND.gold, mt: 0.5, display: 'block', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                                    📖 Review this topic →
                                </Typography>
                            </Box>
                        )}

                        {showAnswer && (
                            <Box mt={3} display="flex" justifyContent="flex-end">
                                <GoldButton onClick={handleNext} endIcon={<ArrowForwardIcon />}>
                                    {current + 1 < questions.length ? 'Next Question' : 'Finish Quiz'}
                                </GoldButton>
                            </Box>
                        )}
                    </CardContent>
                </QuizCard>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper>
            <Typography sx={{ color: BRAND.white, textAlign: 'center', pt: 10, fontFamily: "'Poppins', sans-serif" }}>
                Loading quiz...
            </Typography>
        </PageWrapper>
    );
}

export default JavaScriptQuiz;
